import React, { useEffect, useState } from "react";
import { Headphones, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const categories = [
  "Problema técnico",
  "Pagamento/Premium",
  "Questões",
  "Simulados",
  "Materiais",
  "Conta/Acesso",
  "Sugestão",
  "Outro",
];

export default function SupportPage() {
  const [category, setCategory] = useState("Problema técnico");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [success, setSuccess] = useState(null);

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data.tickets || []);
    } catch {
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const submit = async (e) => {
    e.preventDefault();

    if (subject.trim().length < 3) {
      toast.error("Informe o assunto.");
      return;
    }

    if (message.trim().length < 10) {
      toast.error("Descreva melhor sua dúvida ou problema.");
      return;
    }

    setSending(true);

    try {
      const { data } = await api.post("/support/tickets", {
        category,
        subject: subject.trim(),
        message: message.trim(),
      });

      setSuccess(data);
      setSubject("");
      setMessage("");
      toast.success("Solicitação enviada com sucesso.");
      await loadTickets();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-500/10 text-emerald-400">
            <Headphones className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-white">
              Suporte ApoiaConcursos
            </h1>
            <p className="text-sm text-slate-400">
              Encontrou algum problema ou precisa de ajuda? Fale com a gente.
            </p>
          </div>
        </div>
      </div>

      {success && (
        <div className="mb-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-400" />
            <div>
              <p className="font-bold text-emerald-300">
                Solicitação enviada com sucesso
              </p>
              <p className="mt-1 text-sm text-slate-300">
                Seu protocolo é{" "}
                <strong className="text-white">{success.protocol}</strong>.
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Guarde esse número para acompanhar sua solicitação.
              </p>
            </div>
          </div>
        </div>
      )}

      <section className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6">
        <h2 className="mb-5 text-lg font-bold text-white">
          Abrir solicitação
        </h2>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-300">
              Categoria
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-emerald-500"
            >
              {categories.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-300">
              Assunto
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={150}
              placeholder="Ex.: Não consigo acessar um simulado"
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-600 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-300">
              Descreva sua dúvida ou problema
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              rows={7}
              placeholder="Conte o que aconteceu e, se possível, informe em qual área do ApoiaConcursos ocorreu."
              className="w-full resize-y rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-600 focus:border-emerald-500"
            />
            <p className="mt-1 text-right text-xs text-slate-500">
              {message.length}/5000
            </p>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {sending ? "Enviando..." : "Enviar solicitação"}
          </button>
        </form>
      </section>

      <section className="mt-6 rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6">
        <h2 className="text-lg font-bold text-white">Meus chamados</h2>
        <p className="mt-1 text-sm text-slate-400">
          Acompanhe as solicitações que você já enviou.
        </p>

        {loadingTickets ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-emerald-400" />
          </div>
        ) : tickets.length === 0 ? (
          <p className="mt-5 rounded-xl bg-slate-950/60 p-4 text-sm text-slate-500">
            Você ainda não possui solicitações de suporte.
          </p>
        ) : (
          <div className="mt-5 space-y-3">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="rounded-xl border border-slate-800 bg-slate-950/60 p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-bold text-white">{ticket.subject}</p>
                  <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-bold text-amber-300">
                    {ticket.status}
                  </span>
                </div>

                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span>{ticket.protocol}</span>
                  <span>{ticket.category}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
